import React, { createContext, useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

interface ConfirmDialogOptions {
  title?: string;
  message: string;
  onConfirm?: () => void;
  confirmLabel?: string;
  hideCancel?: boolean;
}

interface ConfirmDialogContextType {
  confirm: (options: ConfirmDialogOptions) => void;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextType | undefined>(undefined);

export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [state, setState] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: (() => void) | null;
    confirmLabel: string;
    hideCancel: boolean;
  }>({
    open: false,
    title: t('dialog.confirm.defaultTitle', 'Confirmation'),
    message: '',
    onConfirm: null,
    confirmLabel: t('dialog.confirm.defaultConfirmLabel', 'Confirm'),
    hideCancel: false,
  });

  const confirm = useCallback((options: ConfirmDialogOptions) => {
    setState({
      open: true,
      title: options.title || t('dialog.confirm.defaultTitle', 'Confirmation'),
      message: options.message,
      onConfirm: options.onConfirm || null,
      confirmLabel: options.confirmLabel || t('dialog.confirm.defaultConfirmLabel', 'Confirm'),
      hideCancel: !!options.hideCancel,
    });
  }, [t]);

  const handleClose = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    if (state.onConfirm) {
      state.onConfirm();
    }
    handleClose();
  }, [state, handleClose]);

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      <Dialog open={state.open} onClose={handleClose}>
        <DialogTitle>{state.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{state.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {!state.hideCancel && <Button onClick={handleClose}>{t('dialog.confirm.cancel', 'Cancel')}</Button>}
          <Button onClick={handleConfirm} color="primary" autoFocus>
            {state.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
};

export const useConfirmDialog = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within a ConfirmDialogProvider');
  }
  return context;
};